// Canonical RCTI document (HTML). This is the SAME layout the admin RCTI page renders
// for the emailed/Xero PDF — kept here so the driver portal produces an IDENTICAL
// document. If you change the official RCTI layout in src/pages/RCTIPage.jsx
// (generatePDFBase64), mirror it here so the portal copy stays in sync.

function fmt(n) {
  if (n === undefined || n === null) return '$0.00';
  const abs = Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return n < 0 ? `-$${abs}` : `$${abs}`;
}
// Advance repayment rows — kept identical to RCTIPage's advanceRowsHtml so the driver's
// portal copy of an RCTI matches the one he was emailed. An advance is a loan repayment,
// so it sits BELOW the GST-inclusive total: the invoice still states the full taxable
// supply and full GST, and only the cash remitted changes.
function advanceRowsHtml(rcti) {
  const a = rcti.advance;
  if (!a || !(a.amount > 0)) return '';
  const net = Math.round(((rcti.total || 0) - a.amount) * 100) / 100;
  return `
        <tr>
          <td style="padding:5px 10px;color:#c00">${a.label || 'Advance Repayment'} <span style="color:#888;font-size:10px">${a.kind === 'overpayment' ? '(correction of an earlier overpayment — not a supply)' : '(repayment of loan — not a supply)'}</span></td>
          <td style="padding:5px 10px;text-align:right;color:#c00">-${fmt(a.amount)}</td>
        </tr>
        <tr style="border-top:2px solid #1a3a6b;background:#eef6ee">
          <td style="padding:8px 10px;font-weight:700;font-size:13px">Net Payment to Bank Account:</td>
          <td style="padding:8px 10px;text-align:right;font-weight:700;font-size:13px;color:#1a3a6b">${fmt(net)}</td>
        </tr>
        <tr>
          <td colspan="2" style="padding:4px 10px;color:#666;font-size:10px">${a.kind === 'overpayment'
            ? `${a.reference || 'Overpayment on an earlier RCTI'}${a.balanceAfter > 0 ? ` · still to recover after this payment: ${fmt(a.balanceAfter)}` : ''}`
            : `Advance outstanding after this payment: ${fmt(a.balanceAfter)}${a.finalDueISO ? ` · repayable in full by ${a.finalDueISO}` : ''}`}</td>
        </tr>`;
}
function fmtRate(n) {
  if (n === undefined || n === null) return '$0.00';
  const abs = Math.abs(n);
  const rounded2 = Math.round(abs * 100) / 100;
  const decimals = Math.abs(abs - rounded2) > 0.0005 ? 4 : 2;
  const [intPart, decPart] = abs.toFixed(decimals).split('.');
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + decPart;
  return n < 0 ? `-$${formatted}` : `$${formatted}`;
}

// The HTML→PDF converter can't render table-cell backgrounds, so the blue header
// banner is drawn as a full-width SVG IMAGE (the one fill the converter DOES render).
// White labels are baked into the SVG; the table below uses a matching colgroup so the
// data lines up under each label.
const RCTI_HEADER_SVG = "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 34'><rect width='1000' height='34' fill='#1a3a6b'/><text x='14' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold'>Description</text><text x='604' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold' text-anchor='end'>Quantity</text><text x='800' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold' text-anchor='end'>Rate (ex GST)</text><text x='986' y='23' fill='#ffffff' font-family='Arial' font-size='15' font-weight='bold' text-anchor='end'>Amount (ex GST)</text></svg>";

const DECLARATION = ['I operate an independent transport business.', 'I invoice for delivery services performed, not for hours worked.', 'I bear commercial risk in operating my business.', 'I am responsible for my own taxation, superannuation and insurances.', 'I may provide services to multiple clients.', 'I may engage or substitute another suitably qualified person to perform the services.', 'Vehicle supplied under separate commercial hire agreement where applicable.', 'Remuneration has been assessed against the applicable Minimum Contract Remuneration Order. Any required adjustment has been included above.'];
const FOOTER = ['The recipient and the supplier declare that this recipient created tax invoice reflects the agreement between the parties.', 'The supplier will not issue tax invoices in respect of the supplies.', 'The supplier acknowledges that it is registered for GST and will notify the recipient if it ceases to be registered.', 'The recipient acknowledges that it is registered for GST and will notify the supplier if it ceases to be registered.'];

// Build the full RCTI HTML. Async because it inlines the HPR logo as a data URI
// (same as the admin page) so the PDF/print carries the logo.
export async function buildRctiHtml(rcti) {
  let logoSrc = '';
  try {
    const logoRes = await fetch('/IMG_5199.jpg');
    const blob = await logoRes.blob();
    logoSrc = await new Promise(r => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(blob); });
  } catch (e) { logoSrc = ''; }
  const today = new Date().toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const lineItemRows = (rcti.lineItems || []).filter(li => li.quantity > 0 || li.amount > 0).map((li, i) =>
    `<tr style="border-bottom:1px solid #ddd;background:${i % 2 === 0 ? '#f9f9f9' : '#fff'}">
        <td style="padding:7px 10px">${li.description}</td>
        <td style="padding:7px 10px;text-align:right">${(li.quantity || 0).toLocaleString()}</td>
        <td style="padding:7px 10px;text-align:right">${fmtRate(li.rate)}</td>
        <td style="padding:7px 10px;text-align:right">${fmt(li.amount)}</td>
      </tr>`
  ).join('');
  const leaseFeeRow = rcti.deductions && rcti.deductions.leaseFee > 0
    ? `<tr><td style="padding:5px 10px;color:#c00">${rcti.deductions.leaseLabel || 'Van Lease Fee'}</td><td style="padding:5px 10px;text-align:right;color:#c00">-${fmt(rcti.deductions.leaseFee)}</td></tr>`
    : '';
  const declarationItems = DECLARATION.map(line =>
    `<div style="margin-bottom:4px;padding-left:12px;border-left:2px solid #1a3a6b;line-height:1.5">${line}</div>`
  ).join('');
  const footerItems = FOOTER.map(line =>
    `<div style="font-style:italic;color:#555;margin-bottom:3px;line-height:1.5">${line}</div>`
  ).join('');
  return `<!DOCTYPE html>
<html><head>
  <style>* { box-sizing: border-box; margin: 0; padding: 0; } body { font-family: Arial, sans-serif; font-size: 11px; color: #000; background: #fff; padding: 14mm 14mm 10mm 14mm; } table { border-collapse: collapse; } @page { size: A4 portrait; margin: 0; }</style>
</head><body>
  <div style="text-align:center;border-bottom:2px solid #1a3a6b;padding-bottom:12px;margin-bottom:16px">
    <div style="font-size:16px;font-weight:700;color:#1a3a6b">RCTI - RECIPIENT CREATED TAX INVOICE</div>
    <div style="font-size:13px;font-weight:600;margin-top:4px">Tax Invoice</div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:16px">
    <div style="border:1px solid #ccc;padding:12px;border-radius:4px">
      <div style="font-weight:700;margin-bottom:6px;color:#1a3a6b">Supplier (contractor)</div>
      <div style="font-weight:600">${rcti.personalName || rcti.driverName || ''}</div>
      <div>Company Name: ${rcti.companyName || '—'}</div>
      <div>ABN: ${rcti.abn || '—'}</div>
      <div style="margin-top:6px">Address: ${rcti.address || '—'}</div>
      <div style="margin-top:6px">Mobile: ${rcti.phone || '—'}</div>
      <div>Email: ${rcti.email || '—'}</div>
    </div>
    <div style="display:flex;flex-direction:column;align-items:flex-end;gap:8px">
<img src="${logoSrc}" alt="HPR" style="height:70px;object-fit:contain" />
      <div style="text-align:right;line-height:1.7">
        <div style="font-weight:600">Hollypaul Recruitment Pty Ltd</div>
        <div>ABN: 60 651 664 885</div>
        <div>PO Box 1710, Warriewood NSW 2102</div>
      </div>
      <div style="border:1px solid #ccc;padding:8px 12px;border-radius:4px;width:100%">
        <div style="display:grid;grid-template-columns:130px 1fr;gap:2px 8px">
          <span style="color:#555">Invoice Number:</span><span style="font-weight:600">${rcti.invoiceNumber}</span>
          <span style="color:#555">Invoice Date:</span><span>${today}</span>
          <span style="color:#555">Billing Period (4 Weeks):</span><span>${rcti.periodLabel}</span>
        </div>
      </div>
    </div>
  </div>
  <img src="data:image/svg+xml;base64,${btoa(RCTI_HEADER_SVG)}" style="width:100%;height:auto;display:block" alt="" />
  <table style="width:100%;margin-bottom:8px;border-collapse:collapse">
    <colgroup><col style="width:46%" /><col style="width:18%" /><col style="width:18%" /><col style="width:18%" /></colgroup>
    <tbody>${lineItemRows}</tbody>
  </table>
  <div style="display:flex;justify-content:flex-end;margin-bottom:16px">
    <table style="width:320px">
      <tbody>
        <tr style="border-top:2px solid #1a3a6b">
          <td style="padding:6px 10px;font-weight:700;color:#1a3a6b">Gross Service Income (ex GST):</td>
          <td style="padding:6px 10px;text-align:right;font-weight:700">${fmt(rcti.grossIncome)}</td>
        </tr>
        ${leaseFeeRow}
        <tr style="border-top:1px solid #ccc">
          <td style="padding:5px 10px">Subtotal (Taxable Supply ex GST):</td>
          <td style="padding:5px 10px;text-align:right;font-weight:600">${fmt(rcti.subtotal)}</td>
        </tr>
        <tr>
          <td style="padding:5px 10px;color:#555">${rcti.gst === 0 ? 'No GST Applicable' : 'GST (10%):'}</td>
          <td style="padding:5px 10px;text-align:right;color:#555">${fmt(rcti.gst)}</td>
        </tr>
        <tr style="border-top:2px solid #1a3a6b;background:#f0f4ff">
          <td style="padding:8px 10px;font-weight:700;font-size:13px">Total Amount Payable (inc GST):</td>
          <td style="padding:8px 10px;text-align:right;font-weight:700;font-size:13px;color:#1a3a6b">${fmt(rcti.total)}</td>
        </tr>${advanceRowsHtml(rcti)}
      </tbody>
    </table>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px">
    <div>
      <div style="font-weight:700;margin-bottom:8px;color:#1a3a6b">Contractor Declaration</div>
      ${declarationItems}
    </div>
    <div style="border:1px solid #ccc;border-radius:4px;padding:12px">
      <div style="font-weight:700;margin-bottom:8px;color:#1a3a6b">Bank Account Details</div>
      <div style="line-height:2">
        <div><strong>Account Name:</strong> ${rcti.companyName || rcti.personalName || ''}</div>
        <div><strong>BSB:</strong> ${rcti.bsb || '—'}</div>
        <div><strong>Acc Number:</strong> ${rcti.accNumber || '—'}</div>
      </div>
    </div>
  </div>
  <div style="border-top:1px solid #ccc;padding-top:12px">${footerItems}</div>
</body></html>`;
}
